import { MantaProvider } from '@happy-ops-org/manta-react'
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import { useState } from 'react'
import { CollectionEditor } from './CollectionEditor.js'
import { OrcaQueryEditor } from './OrcaQueryEditor.js'
import { type Credentials, LoginScreen } from './LoginScreen.js'
import { mantaUrl, type MantaServer } from './serverStore.js'

type Session = {
  server: MantaServer
  credentials: Credentials
}

type Tab = 'collections' | 'queries'

export const App = () => {
  const [session, setSession] = useState<Session | null>(null)
  const [error, setError] = useState<string | undefined>(undefined)
  const [tab, setTab] = useState<Tab>('collections')

  if (!session) {
    return (
      <LoginScreen
        error={error}
        onConnect={(server, credentials) => {
          setError(undefined)
          setSession({ server, credentials })
        }}
      />
    )
  }

  const { server, credentials } = session

  const backToLogin = (message?: string) => {
    setError(message)
    setSession(null)
  }

  return (
    <MantaProvider
      url={mantaUrl(server)}
      mantaConfig={{
        writerName: 'mcp-collection-editor',
        auth: {
          username: credentials.username,
          password: credentials.password,
        },
        type: 'ws',
        cxn: {
          type: 'fail-and-report',
          onConnectionFailed: (err) =>
            console.error('Manta connection failed', err),
          timeoutMs: 10_000,
        },
        requiredTables: ['mcp_collection_meta', 'mcp_orca_query'],
      }}
      onFailed={() => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          flexDirection="column"
          gap={2}
        >
          <Typography color="error" variant="h6">
            Verbindung fehlgeschlagen
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Server{' '}
            <code>
              {server.port ? `${server.host}:${server.port}` : server.host}
            </code>{' '}
            als <code>{credentials.username}</code>.
          </Typography>
          <Button variant="contained" onClick={() => backToLogin()}>
            Zurück zur Anmeldung
          </Button>
        </Box>
      )}
      onLoading={
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          gap={2}
        >
          <CircularProgress size={24} />
          <Typography color="text.secondary">Verbinde mit Manta…</Typography>
        </Box>
      }
    >
      <Box display="flex" flexDirection="column" height="100vh">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid"
          borderColor="divider"
          pr={1}
        >
          <Tabs value={tab} onChange={(_, v) => setTab(v as Tab)}>
            <Tab label="Collections (Manta)" value="collections" />
            <Tab label="Abfragen (Orca)" value="queries" />
          </Tabs>
          <Button
            size="small"
            startIcon={<LogoutIcon />}
            onClick={() => backToLogin()}
          >
            Abmelden
          </Button>
        </Box>
        <Box flex={1} minHeight={0}>
          {tab === 'collections' ? <CollectionEditor /> : <OrcaQueryEditor />}
        </Box>
      </Box>
    </MantaProvider>
  )
}
